import { intervalToDuration } from "date-fns";
import { ja, enUS } from "date-fns/locale";
import { timeAgo as timeAgoOrigin } from "@scrile/tools/dist/lib/TimeHelpers";
import { $t } from "@/lib/i18nHelpers";
import { getLocale } from "@/lib/localeHelper";
import { TIME_MEASURAND } from "@/const";

export * from "@scrile/tools/dist/lib/TimeHelpers";

const PST_GMS_DIFF = -8;

export function dateToPST(date: Date): Date {
  const d = new Date(date.getTime());
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset() + PST_GMS_DIFF * 60);
  return d;
}

export function formatDate(date: Date, options?: Partial<Intl.DateTimeFormatOptions>): string {
  return date.toLocaleString(getLocale(), options);
}

export function timeAgo(
  time: string | number | Date,
  { addSuffix, locale }: { addSuffix: boolean; locale?: string } = { addSuffix: false }
): string {
  let localeOptions;
  const localeName = locale || getLocale();

  if (localeName === "ja") localeOptions = ja;
  else localeOptions = enUS;
  if (Date.now() - new Date(time).getTime() < 1000) return $t("just now");

  return timeAgoOrigin(time, { addSuffix, locale: localeOptions });
}

export function formatTimeInterval(inputTime: number, inputMeasurand: TIME_MEASURAND = TIME_MEASURAND.MS) {
  const now = new Date();
  let duration = 0;
  let formattedText = "";

  switch (inputMeasurand) {
    case TIME_MEASURAND.D:
      duration = inputTime * 24 * 60 * 60 * 1000;
      break;
    case TIME_MEASURAND.H:
      duration = inputTime * 60 * 60 * 1000;
      break;
    case TIME_MEASURAND.M:
      duration = inputTime * 60 * 1000;
      break;
    case TIME_MEASURAND.S:
      duration = inputTime * 1000;
      break;
    case TIME_MEASURAND.MS:
      duration = inputTime;
      break;
  }

  const interval = intervalToDuration({
    start: now,
    end: new Date(now.getTime() + duration),
  });

  Object.entries(interval).forEach(measurand => {
    formattedText += measurand[1]
      ? ` ${measurand[1]} ${measurand[1] > 1 ? measurand[0] : measurand[0].slice(0, -1)}`
      : "";
  });

  return formattedText.trim() ? formattedText.trim() : $t("0 second");
}

export const getNow = () => new Date();

function padDatePart(value: number): string {
  return String(value).padStart(2, "0");
}

export function parseDateOnly(value: string | Date): Date {
  const date = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00.000Z`)
    : new Date(value);

  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export function formatDateOnly(value: string | Date, pattern = "yyyy-MM-dd"): string {
  const date = parseDateOnly(value);
  const year = String(date.getUTCFullYear());
  const month = padDatePart(date.getUTCMonth() + 1);
  const day = padDatePart(date.getUTCDate());

  return pattern.replace("yyyy", year).replace("MM", month).replace("dd", day);
}

export function toDateOnlyISO(value: string): string {
  return `${formatDateOnly(value)}T00:00:00.000Z`;
}
